import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FieldArray, getFormValues, change } from 'redux-form';
import classnames from 'classnames';
import { Button, Col, FormGroup, FormText, Label, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { JsonEditorField } from '../../components';
import { getDownstreamBricksInFlowOrder, stableBrickNodeId } from '../../containers/Workflows/workflowFlowUtils';

const FORM_NAME = 'workflowForm';

/** @param {string} member e.g. bricks[0].options.custom_variables[1] */
function parse_custom_variable_member(member) {
    const m = String(member).match(/^bricks\[(\d+)]\.options\.custom_variables\[(\d+)]$/);
    if (!m)
        return null;
    return { brick_index: parseInt(m[1], 10), row_index: parseInt(m[2], 10) };
}

function custom_variable_row_type(state, member) {
    const parsed = parse_custom_variable_member(member);
    if (!parsed)
        return 'string';
    const vals = getFormValues(FORM_NAME)(state);
    const row = vals?.bricks?.[parsed.brick_index]?.options?.custom_variables?.[parsed.row_index];
    return row?.type || 'string';
}

/** @param {string} typ */
function json_editor_value_for_type(typ, value) {
    if (typ === 'array') {
        if (value === undefined || value === null || value === '')
            return [];
        if (Array.isArray(value))
            return value;
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    }
    if (typ === 'object') {
        if (value === undefined || value === null || value === '')
            return {};
        if (typeof value === 'object' && !Array.isArray(value))
            return value;
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
            } catch {
                return {};
            }
        }
        return {};
    }
    if (typ === 'json') {
        if (value === undefined || value === null || value === '')
            return {};
        if (typeof value === 'object')
            return value;
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return {};
            }
        }
        return {};
    }
    return value;
}

/**
 * Inner tabs: detected downstream inputs vs manually added workflow variables.
 *
 * @param {{ brick: object, workflow: object, t: function }} props
 */
export default function ContextSetDataOptionsForm({ brick, workflow, t }) {
    const position = brick.position;
    const [activeTab, setActiveTab] = useState('detected');

    const downstream = useMemo(
        () =>
            getDownstreamBricksInFlowOrder(
                workflow?.bricks,
                workflow?.editor_layout,
                stableBrickNodeId(brick)
            ),
        [workflow?.bricks, workflow?.editor_layout, brick]
    );

    const detectedKeys = useMemo(() => {
        const set = new Set();
        downstream.forEach((b) => {
            (b.inputs || []).forEach((input) => set.add(input));
        });
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [downstream]);

    const detectedSet = useMemo(() => new Set(detectedKeys), [detectedKeys]);

    const optionsSnapshot = useSelector(
        (state) => state.form?.[FORM_NAME]?.values?.bricks?.[position]?.options
    );

    const customRows = optionsSnapshot?.custom_variables;
    const overlappingCustomKeys = useMemo(() => {
        if (!Array.isArray(customRows))
            return [];
        const dup = [];
        customRows.forEach((row) => {
            const k = row?.key != null ? String(row.key).trim() : '';
            if (k && detectedSet.has(k))
                dup.push(k);
        });
        return dup;
    }, [customRows, detectedSet]);

    const base = `bricks[${position}].options`;

    return (
        <div className="ContextSetDataOptionsForm">
            <Nav tabs className="mb-2">
                <NavItem>
                    <NavLink
                        href="#"
                        className={classnames({ active: activeTab === 'detected' })}
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('detected');
                        }}
                    >
                        {t('workflows.bricks.context.set.data.detected_values_tab')}
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        href="#"
                        className={classnames({ active: activeTab === 'custom' })}
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('custom');
                        }}
                    >
                        {t('workflows.bricks.context.set.data.custom_variables_tab')}
                    </NavLink>
                </NavItem>
            </Nav>
            <TabContent activeTab={activeTab}>
                <TabPane tabId="detected">
                    <FormText color="muted" className="mb-2">
                        {t('workflows.bricks.context.set.data.detected_values_hint')}
                    </FormText>
                    {detectedKeys.length === 0 ? (
                        <p className="text-muted small mb-0">
                            {t('workflows.bricks.context.set.data.detected_empty')}
                        </p>
                    ) : (
                        detectedKeys.map((keyName) => (
                            <FormGroup key={`det-${keyName}`} row>
                                <Label for={`ctx-det-${position}-${keyName}`} className="text-v-center" sm={12}>
                                    {keyName}
                                </Label>
                                <Col sm={12}>
                                    <Field
                                        component="input"
                                        type="text"
                                        name={`${base}.${keyName}`}
                                        id={`ctx-det-${position}-${keyName}`}
                                        className="form-control"
                                    />
                                </Col>
                            </FormGroup>
                        ))
                    )}
                </TabPane>
                <TabPane tabId="custom">
                    <FormText color="muted" className="mb-2">
                        {t('workflows.bricks.context.set.data.custom_variables_hint')}
                    </FormText>
                    {overlappingCustomKeys.length > 0 ? (
                        <p className="small text-warning mb-2">
                            {t('workflows.bricks.context.set.data.overlaps_detected')}
                        </p>
                    ) : null}
                    <FieldArray
                        name={`${base}.custom_variables`}
                        component={CustomVariablesFieldArray}
                        t={t}
                        detectedSet={detectedSet}
                    />
                </TabPane>
            </TabContent>
        </div>
    );
}

/** Value control for one custom row; `type` is read from form state so the widget updates when Type changes. */
function CustomVariableValueField({ input, member, t, rowInputId }) {
    const typ = useSelector((state) => custom_variable_row_type(state, member));
    if (typ === 'number') {
        return (
            <input
                {...input}
                type="number"
                step="any"
                className="form-control"
                id={rowInputId}
            />
        );
    }
    if (typ === 'boolean') {
        return (
            <select {...input} className="form-control" id={rowInputId} value={input.value ?? ''}>
                <option value="">{t('workflows.bricks.context.set.data.boolean_placeholder')}</option>
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
            </select>
        );
    }
    if (typ === 'object' || typ === 'array' || typ === 'json') {
        const adapted = {
            ...input,
            value: json_editor_value_for_type(typ, input.value),
        };
        return (
            <div id={rowInputId} key={`${member}-${typ}`}>
                <JsonEditorField input={adapted} />
                {typ === 'object' ? (
                    <FormText color="muted" className="mt-1 mb-0">
                        {t('workflows.bricks.context.set.data.object_value_help')}
                    </FormText>
                ) : null}
                {typ === 'array' ? (
                    <FormText color="muted" className="mt-1 mb-0">
                        {t('workflows.bricks.context.set.data.array_value_help')}
                    </FormText>
                ) : null}
                {typ === 'json' ? (
                    <FormText color="muted" className="mt-1 mb-0">
                        {t('workflows.bricks.context.set.data.json_value_help')}
                    </FormText>
                ) : null}
            </div>
        );
    }
    return <input {...input} type="text" className="form-control" id={rowInputId} />;
}

function CustomVariableTypeField({ input, member, t }) {
    const dispatch = useDispatch();
    return (
        <select
            {...input}
            value={input.value || 'string'}
            onChange={(e) => {
                const next = e.target.value;
                input.onChange(next);
                if (next === 'object') {
                    dispatch(change(FORM_NAME, `${member}.value`, {}));
                } else if (next === 'array') {
                    dispatch(change(FORM_NAME, `${member}.value`, []));
                } else if (next === 'json') {
                    dispatch(change(FORM_NAME, `${member}.value`, {}));
                }
            }}
            className="form-control"
        >
            <option value="string">{t('workflows.bricks.context.set.data.type_string')}</option>
            <option value="number">{t('workflows.bricks.context.set.data.type_number')}</option>
            <option value="boolean">{t('workflows.bricks.context.set.data.type_boolean')}</option>
            <option value="object">{t('workflows.bricks.context.set.data.type_object')}</option>
            <option value="array">{t('workflows.bricks.context.set.data.type_array')}</option>
            <option value="json">{t('workflows.bricks.context.set.data.type_json_legacy')}</option>
        </select>
    );
}

function CustomVariablesFieldArray({ fields, t, detectedSet }) {
    return (
        <>
            {fields.map((member, index) => {
                const rowInputId = `context-custom-val-${String(fields.name || 'row').replace(/[^a-zA-Z0-9]/g, '-')}-${index}`;
                return (
                <div key={member} className="border rounded p-2 mb-2">
                    <FormGroup className="mb-2">
                        <Label className="small mb-1" for={`${member}.key`}>
                            {t('workflows.bricks.context.set.data.variable_name')}
                        </Label>
                        <Field
                            name={`${member}.key`}
                            id={`${member}.key`}
                            component={({ input }) => (
                                <>
                                    <input {...input} type="text" className="form-control" />
                                    {input.value != null &&
                                    String(input.value).trim() !== '' &&
                                    detectedSet.has(String(input.value).trim()) ? (
                                        <small className="text-warning d-block mt-1">
                                            {t('workflows.bricks.context.set.data.overlap_this_row')}
                                        </small>
                                    ) : null}
                                </>
                            )}
                        />
                    </FormGroup>
                    <FormGroup className="mb-2">
                        <Label className="small mb-1" for={`${member}.type`}>
                            {t('workflows.bricks.context.set.data.value_type')}
                        </Label>
                        <Field
                            name={`${member}.type`}
                            id={`${member}.type`}
                            component={CustomVariableTypeField}
                            member={member}
                            t={t}
                        />
                    </FormGroup>
                    <FormGroup className="mb-2">
                        <Label className="small mb-1" htmlFor={rowInputId}>
                            {t('workflows.bricks.context.set.data.initial_value')}
                        </Label>
                        <Field
                            name={`${member}.value`}
                            component={CustomVariableValueField}
                            member={member}
                            t={t}
                            rowInputId={rowInputId}
                        />
                    </FormGroup>
                    <Button type="button" size="sm" color="danger" onClick={() => fields.remove(index)}>
                        <i className="fa fa-trash" aria-hidden="true" /> {t('common.delete')}
                    </Button>
                </div>
                );
            })}
            <Button
                type="button"
                size="sm"
                color="info"
                onClick={() => fields.push({ key: '', value: '', type: 'string' })}
            >
                <i className="fa fa-plus" aria-hidden="true" /> {t('workflows.bricks.context.set.data.custom_add_row')}
            </Button>
        </>
    );
}
